import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flame, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { siteData } from '../mock';

const Navbar = ({ onBook }) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const linkClass =
    "text-white/90 text-[15px] hover:text-white relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[1px] after:bg-white after:transition-all hover:after:w-full";

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled ? 'bg-[#0b1230]/85 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-12 py-4 md:py-6 gap-3 md:gap-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group flex-1 min-w-0">
          <Flame className="h-6 w-6 md:h-7 md:w-7 text-white flex-shrink-0" strokeWidth={2.5} />
          <span className="text-white font-extrabold tracking-wide text-[13px] md:text-[15px] leading-none truncate">
            {siteData.brand.name}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center justify-center gap-8 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger className="outline-none flex items-center gap-1 text-white/90 text-[15px] hover:text-white transition-colors">
              About <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="min-w-[220px] mt-2">
              <DropdownMenuItem
                onClick={() => navigate('/about/what-we-believe')}
                className="cursor-pointer py-2.5"
              >
                What We Believe
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger className="outline-none flex items-center gap-1 text-white/90 text-[15px] hover:text-white transition-colors">
              Ministries <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="min-w-[240px] mt-2">
              <DropdownMenuItem onClick={() => navigate('/ministries/outreach')} className="cursor-pointer py-2.5">Outreach</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/ministries/act-children')} className="cursor-pointer py-2.5">ACT Children</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/ministries/reach-youth')} className="cursor-pointer py-2.5">Reach Youth Ministry</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/ministries/media')} className="cursor-pointer py-2.5">Media</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/ministries')} className="cursor-pointer py-2.5 border-t border-black/5 mt-1 font-medium">
                View all →
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/contact" className={linkClass}>Contact</Link>
          <Link to="/give" className={linkClass}>Give</Link>
          <Link to="/merch" className={linkClass}>Merch</Link>
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 md:gap-5 flex-1 justify-end">
          <Button
            onClick={onBook}
            className="hidden sm:inline-flex rounded-full bg-[#e9e3d7] hover:bg-white text-[#1a1a1a] px-6 py-5 h-auto text-[14px] font-medium shadow-sm"
          >
            Plan a visit
          </Button>

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] max-w-[380px] bg-[#0a1030] text-white border-l border-white/10 p-0 overflow-y-auto">
              {/* Header */}
              <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b border-white/10">
                <div className="flex items-center gap-2">
                  <Flame className="h-6 w-6" strokeWidth={2.5} />
                  <span className="font-extrabold tracking-wide text-[13px]">ACT</span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Menu items */}
              <nav className="px-2 py-4">
                <Accordion type="multiple" className="w-full">
                  <AccordionItem value="about" className="border-b border-white/10">
                    <AccordionTrigger className="px-4 py-4 text-white text-[17px] font-medium hover:no-underline">
                      About
                    </AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <button
                        onClick={() => go('/about/what-we-believe')}
                        className="block w-full text-left px-8 py-2.5 text-white/80 hover:text-white hover:bg-white/5 rounded-md text-[15px]"
                      >
                        What We Believe
                      </button>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="ministries" className="border-b border-white/10">
                    <AccordionTrigger className="px-4 py-4 text-white text-[17px] font-medium hover:no-underline">
                      Ministries
                    </AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <button onClick={() => go('/ministries/outreach')} className="block w-full text-left px-8 py-2.5 text-white/80 hover:text-white hover:bg-white/5 rounded-md text-[15px]">Outreach</button>
                      <button onClick={() => go('/ministries/act-children')} className="block w-full text-left px-8 py-2.5 text-white/80 hover:text-white hover:bg-white/5 rounded-md text-[15px]">ACT Children</button>
                      <button onClick={() => go('/ministries/reach-youth')} className="block w-full text-left px-8 py-2.5 text-white/80 hover:text-white hover:bg-white/5 rounded-md text-[15px]">Reach Youth Ministry</button>
                      <button onClick={() => go('/ministries/media')} className="block w-full text-left px-8 py-2.5 text-white/80 hover:text-white hover:bg-white/5 rounded-md text-[15px]">Media</button>
                      <button onClick={() => go('/ministries')} className="block w-full text-left px-8 py-2.5 text-white/90 hover:text-white hover:bg-white/5 rounded-md text-[15px] font-medium mt-1 border-t border-white/10 pt-3">View all →</button>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <button onClick={() => go('/contact')} className="block w-full text-left px-4 py-4 text-white text-[17px] font-medium border-b border-white/10 hover:bg-white/5">Contact</button>
                <button onClick={() => go('/give')} className="block w-full text-left px-4 py-4 text-white text-[17px] font-medium border-b border-white/10 hover:bg-white/5">Give</button>
                <button onClick={() => go('/merch')} className="block w-full text-left px-4 py-4 text-white text-[17px] font-medium border-b border-white/10 hover:bg-white/5">Merch</button>
              </nav>

              {/* Plan a visit CTA */}
              <div className="px-6 py-6">
                <Button
                  onClick={() => {
                    setOpen(false);
                    onBook?.();
                  }}
                  className="w-full rounded-full bg-[#e9e3d7] hover:bg-white text-[#0a1030] h-12 text-[15px] font-semibold"
                >
                  Plan a visit
                </Button>
                <p className="mt-4 text-center text-xs text-white/50">
                  370 Tomkins Court, Gilroy CA
                </p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
